/**
 * Bundle colour picker: five slots open a "Choose your shades" sheet where five shades are picked.
 * Display only: nothing is added to the cart.
 */

const MAX = 5;
const PICKS = '.offer-card__picks';

/** @type {WeakMap<Element, string[]>} */
const selections = new WeakMap();

/** @param {Element} picks */
function getSelection(picks) {
  let list = selections.get(picks);
  if (!list) {
    list = [];
    selections.set(picks, list);
  }
  return list;
}

/** @param {HTMLElement} picks */
function render(picks) {
  const list = getSelection(picks);
  picks.dataset.shades = list.join('|');
  const imageOf = (name) =>
    picks.querySelector(`.shade-tile[data-shade="${CSS.escape(name)}"]`)?.getAttribute('data-image') ?? '';

  picks.querySelectorAll('.shade-slot').forEach((slot, index) => {
    const name = list[index];
    slot.classList.toggle('shade-slot--filled', Boolean(name));
    slot.setAttribute('aria-label', name ? `Shade ${index + 1}: ${name}` : `Choose shade ${index + 1}`);

    const image = slot.querySelector('.shade-slot__nail img');
    const label = slot.querySelector('.shade-slot__name');
    if (image instanceof HTMLImageElement) {
      if (name) image.src = imageOf(name);
      else image.removeAttribute('src');
    }
    if (label) label.textContent = name ?? '';
  });

  picks.querySelectorAll('.shade-tile').forEach((tile) => {
    const amount = list.filter((name) => name === tile.getAttribute('data-shade')).length;
    tile.setAttribute('aria-pressed', String(amount > 0));
    const badge = tile.querySelector('.shade-tile__check');
    if (badge) badge.textContent = amount > 0 ? String(amount) : '';
  });

  const count = picks.querySelector('.shade-sheet__count');
  if (count) count.textContent = `${list.length}/${MAX}`;

  // Let the rest of the page (e.g. the Add to cart button) know how many shades are chosen.
  picks.dataset.count = String(list.length);
  document.dispatchEvent(new CustomEvent('offer:shades-changed'));

  const confirm = picks.querySelector('.shade-sheet__confirm');
  if (confirm instanceof HTMLButtonElement) {
    const missing = MAX - list.length;
    confirm.disabled = missing > 0;
    confirm.textContent = missing > 0 ? `Choose ${missing} more shade${missing === 1 ? '' : 's'}` : 'Confirm shades';
  }
}

/** @param {HTMLDialogElement} dialog */
function closeSheet(dialog) {
  if (dialog.open) dialog.close();
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  // Click on the backdrop (the dialog element itself) closes the sheet.
  if (target instanceof HTMLDialogElement && target.classList.contains('shade-sheet')) {
    closeSheet(target);
    return;
  }

  const slot = target.closest('.shade-slot');
  if (slot) {
    const picks = slot.closest(PICKS);
    const dialog = picks?.querySelector('dialog.shade-sheet');
    if (picks instanceof HTMLElement && dialog instanceof HTMLDialogElement) {
      render(picks);
      document.documentElement.style.overflow = 'hidden';
      dialog.showModal();
    }
    return;
  }

  const tile = target.closest('.shade-tile');
  if (tile) {
    const picks = tile.closest(PICKS);
    if (!(picks instanceof HTMLElement)) return;
    const list = getSelection(picks);
    const name = tile.getAttribute('data-shade') ?? '';

    if (target.closest('.shade-tile__minus')) {
      // Remove one of this shade (the last one picked).
      const at = list.lastIndexOf(name);
      if (at >= 0) list.splice(at, 1);
    } else if (list.length < MAX) {
      // The same shade can be picked more than once.
      list.push(name);
    }

    render(picks);
    return;
  }

  if (target.closest('.shade-sheet__close') || target.closest('.shade-sheet__confirm')) {
    const dialog = target.closest('dialog.shade-sheet');
    if (dialog instanceof HTMLDialogElement) closeSheet(dialog);
  }
});

// Give the page its scroll back whenever a sheet closes (button, backdrop or Escape).
document.addEventListener(
  'close',
  (event) => {
    if (event.target instanceof HTMLDialogElement && event.target.classList.contains('shade-sheet')) {
      document.documentElement.style.overflow = '';
    }
  },
  true
);

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  const target = event.target instanceof Element ? event.target : null;
  if (target?.matches('.shade-tile__minus')) {
    event.preventDefault();
    target.click();
  }
});
